import { useState } from "react";
import { useGetIngredientsQuery } from "@/redux/services/inventory/ingredient.api";
import { useGetInventoryValuationQuery } from "@/redux/services/inventory/inventoryReport.api";
import { AlertTriangle, Package, Wallet } from "lucide-react";
import { displayPrice } from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import LowStockAlert from "./LowStockAlert";
import IngredientsList from "./IngredientsList";

interface InventoryDashboardProps {
  restaurantId: string;
}

const InventoryDashboard = ({ restaurantId }: InventoryDashboardProps) => {
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const currency = useRestaurantCurrency();

  const { data: totalData } = useGetIngredientsQuery({
    restaurantId,
    query: { limit: 1 },
  });
  const { data: lowStockData } = useGetIngredientsQuery({
    restaurantId,
    query: { lowStock: true, limit: 1 },
  });
  const { data: valuation } = useGetInventoryValuationQuery({ restaurantId });

  const totalIngredients = totalData?.count ?? 0;
  const lowStockCount = lowStockData?.count ?? 0;
  const totalValue = valuation?.totalValue ?? 0;

  const handleToggleLowStock = () => {
    setShowLowStockOnly((prev) => !prev);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="dash__card flex items-center gap-4">
          <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
            <Package className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total ingrédients</p>
            <p className="text-2xl font-semibold text-card-foreground">
              {totalIngredients}
            </p>
          </div>
        </div>

        <div className="dash__card flex items-center gap-4">
          <div className="h-12 w-12 rounded-lg bg-orange-100 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Stock faible</p>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-semibold text-card-foreground">
                {lowStockCount}
              </p>
              {lowStockCount > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  Attention
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="dash__card flex items-center gap-4">
          <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
            <Wallet className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Valeur du stock</p>
            <p className="text-2xl font-semibold text-card-foreground">
              {displayPrice(totalValue, currency)}
            </p>
          </div>
        </div>
      </div>

      {lowStockCount > 0 && !showLowStockOnly && (
        <LowStockAlert count={lowStockCount} onClick={handleToggleLowStock} />
      )}

      <IngredientsList
        restaurantId={restaurantId}
        showLowStockOnly={showLowStockOnly}
        onToggleLowStock={handleToggleLowStock}
      />
    </div>
  );
};

export default InventoryDashboard;
